import type {OutlookRecipient} from '../EmailScreen/Content/EmailRecipients/useOutlookEmailRecipients';
import type {
  CompanyLink,
  DealLink,
  JobLink,
  MessageAttachment,
  PersonLink,
} from '../SendModal/EmailSync/structs';
import type {PodPostRequestFn} from '../usePodRequest';

const OUTLOOK_EMAIL_SYNCS_PATH = '/outlook_add_in/outlook_email_syncs';

export type CreateOutlookEmailSyncParams = {
  agencyId: number;
  headerId: string;
  sync: boolean;
  subject: string;
  fromAddress: string;
  personLinks: PersonLink[];
  companyLinks: CompanyLink[];
  jobLinks: JobLink[];
  dealLinks: DealLink[];
  recipients: OutlookRecipient[];
  attachments: MessageAttachment[];
};

export type CreateOutlookEmailSyncJson = {
  ok: boolean;
  errors: string[];
};

const attachmentAttributes = (
  attachmentIds: readonly string[],
  attachments: readonly MessageAttachment[]
) => {
  return attachmentIds
    .map((attachmentId) => attachments.find((att) => att.id === attachmentId))
    .filter((att): att is MessageAttachment => att != null)
    .map((att) => ({
      file_name: att.name,
      file_size: att.size,
      file_content_type: att.contentType,
    }));
};

const buildOutlookEmailSyncBody = (params: CreateOutlookEmailSyncParams) => ({
  outlook_email_sync: {
    header_id: params.headerId,
    sync: params.sync,
    subject: params.subject,
    from_address: params.fromAddress,
    outlook_email_sync_person_links_attributes: params.personLinks.map(
      (link) => ({
        person_id: link.person.id,
        pinned: link.pinned,
        outlook_email_sync_person_link_attachments_attributes:
          attachmentAttributes(link.attachmentIds, params.attachments),
      })
    ),
    outlook_email_sync_company_links_attributes: params.companyLinks.map(
      (link) => ({
        company_id: link.company.id,
        pinned: link.pinned,
        outlook_email_sync_company_link_attachments_attributes:
          attachmentAttributes(link.attachmentIds, params.attachments),
      })
    ),
    outlook_email_sync_job_links_attributes: params.jobLinks.map((link) => ({
      job_id: link.job.id,
      pinned: link.pinned,
      outlook_email_sync_job_link_attachments_attributes: attachmentAttributes(
        link.attachmentIds,
        params.attachments
      ),
    })),
    outlook_email_sync_deal_links_attributes: params.dealLinks.map((link) => ({
      deal_id: link.deal.id,
      pinned: false,
      outlook_email_sync_deal_link_attachments_attributes: attachmentAttributes(
        link.attachmentIds,
        params.attachments
      ),
    })),
    outlook_email_sync_recipients_attributes: params.recipients.map(
      (recipient) => ({
        email_address: recipient.email,
        to: recipient.field === 'to',
        cc: recipient.field === 'cc',
        bcc: recipient.field === 'bcc',
      })
    ),
  },
});

export const createOutlookEmailSync = async (
  postRequest: PodPostRequestFn,
  params: CreateOutlookEmailSyncParams
): Promise<CreateOutlookEmailSyncJson> => {
  const searchParams = new URLSearchParams();
  searchParams.set('agency_id', String(params.agencyId));
  const response = await postRequest(
    `${OUTLOOK_EMAIL_SYNCS_PATH}?${searchParams.toString()}`,
    buildOutlookEmailSyncBody(params)
  );
  const text = await response.text();
  if (!text) {
    throw new Error(`Request failed (${response.status})`);
  }
  const body = JSON.parse(text) as CreateOutlookEmailSyncJson;
  if (!response.ok) {
    const message =
      body.errors?.length > 0
        ? body.errors.join(', ')
        : `Request failed (${response.status})`;
    throw new Error(message);
  }
  return body;
};
